<script lang="ts" setup>
  import { type PropType, computed } from 'vue';
  import { useBreakpoints } from '@storefront/storefront';
  import type { CustomerBillingAddress, Country } from '@storefront/types';
  import { Features } from '@shared/features';
  import { StoreLayout, AccountLayout } from '@storefront/components';
  import { useHttpClient, useConfirmation, useTranslator, useRoute } from '@othercommerce/foundation';


  defineOptions({
    layout: [StoreLayout, AccountLayout],
    inheritAttrs: false,
  });

  defineProps({
    addresses: { type: Array as PropType<CustomerBillingAddress[]>, required: true },
    countries: { type: Array as PropType<Country[]>, required: true },
  });

  const breakpoints = useBreakpoints();
  const http = useHttpClient();
  const confirm = useConfirmation();
  const translator = useTranslator();
  const route = useRoute();

  const canCreateBillingAddress = computed(() => Features.streamsoft.isDisabled);
  const canDeleteBillingAddress = computed(() => Features.streamsoft.isDisabled);

  function destroy(addressId: number) {
    confirm({
      callback: () => http.delete(route('web.account.address.billing.destroy', { billing: addressId })),
      title: translator.trans('SHOP.ACCOUNT.HEADER.BILLING_ADDRESS_DESTROY'),
      description: translator.trans('SHOP.ACCOUNT.MESSAGE.BILLING_ADDRESS_DESTROY'),
      yes: translator.trans('SHOP.ACTION.DESTROY'),
      no: translator.trans('SHOP.ACTION.CANCEL'),
    }).catch(() => void (0));
  }
</script>
<template>
  <AccountMobileNavigation :href="$route('web.account.show')">
    {{ $t('SHOP.ACCOUNT.HEADER.BILLING_ADDRESSES') }}
  </AccountMobileNavigation>
  <SfTile class="account-billing" large>
    <template v-if="breakpoints.lg" #title>
      <h1 class="account__heading">
        {{ $t('SHOP.ACCOUNT.HEADER.BILLING_ADDRESSES') }}
      </h1>
    </template>
    <template #headerAction>
      <div class="account-billing__new">
        <SfButton v-if="canCreateBillingAddress" :href="$route('web.account.address.billing.create')" class="account-billing__new-button" primary>
          <template #before>
            <SfIconPlus aria-hidden="true" />
          </template>
          <template #default>
            {{ $t('SHOP.ACTION.CREATE') }}
          </template>
        </SfButton>
      </div>
    </template>
    <template #default>
      <div v-if="addresses.length > 0" class="account-billing__addresses">
        <SfTile v-for="address in addresses" class="account-billing__address" shadow>
          <template #title>
            <h2 class="account-billing__address-name">
              {{ address.company || address.name }}
            </h2>
          </template>
          <template #default>
            <address class="account-billing__address-data">
              <p v-if="address.firstLine" class="account-billing__address-first-line">{{ address.firstLine }}</p>
              <p v-if="address.secondLine" class="account-billing__address-second-line">{{ address.secondLine }}</p>
              <p v-if="address.thirdLine" class="account-billing__address-third-line">{{ address.thirdLine }}</p>
              <p v-if="address.email" class="account-billing__address-email">{{ address.email }}</p>
              <p v-if="address.phone" class="account-billing__address-phone">{{ address.phone.rfc }}</p>
              <p v-if="address.vatId" class="account-billing__address-nip">{{ $t('SHOP.ACCOUNT.LABEL.VAT_ID') }} {{ address.vatId }}</p>
              <p v-if="address.countryCode" class="account-billing__address-code">{{ address.countryCode }}</p>
            </address>
          </template>
          <template #footer>
            <SfButton v-if="canDeleteBillingAddress" :aria-label="$t('SHOP.ACCOUNT.ARIA.REMOVE_INVOICE_DATA')" danger rounded square transparent @click="destroy(address.id)">
              <template #before>
                <SfIconTrash aria-hidden="true" class="account-billing__icon" />
              </template>
              <template #default>
                {{ $t('SHOP.ACTION.DESTROY') }}
              </template>
            </SfButton>
            <SfButton :href="$route('web.account.address.billing.show', { billing: address.id})">
              <template #before>
                <SfIconPen aria-hidden="true" />
              </template>
              <template #default>
                {{ $t('SHOP.ACTION.EDIT') }}
              </template>
            </SfButton>
          </template>
        </SfTile>
      </div>
      <SfDataPlaceholder v-else>
        {{ $t('SHOP.ACCOUNT.MESSAGE.BILLING_ADDRESS_PLACEHOLDER') }}
      </SfDataPlaceholder>
    </template>
  </SfTile>

  <RouterView />
</template>
<style lang="scss">
  @use "@storefront/styles/mixins/media";

  .account-billing {
    &__addresses {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
      gap: 2rem;
    }

    &__address {
      display: flex;
      position: relative;
    }

    &__address-name {
      display: -webkit-box;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 2;
      overflow: hidden;
      text-overflow: ellipsis;
      max-width: 100%;
      font-size: 1.25rem;
    }

    &__address-data p,
    &__address-data {
      font-size: 0.875rem;
    }
  }

  @include media.query(xxl) {
    .account-billing .account-billing__addresses {
      gap: 1rem
    }
  }

  @include media.query(lg) {
    .account-billing {
      &__new {
        display: flex;
        justify-content: flex-end;
        width: 100%;
      }

      &__new-button {
        margin-bottom: 1rem;
      }

      &__address .tile__outer {
        padding: 0.25rem 0.5rem;
      }

      &__address-name {
        font-size: 1.125rem;
      }
    }
  }
</style>
